// WorkBrowserPreview — the live thumbnail of the agent's browser that sits in
// the Sources/Outputs rail on desktop web. Click it to open the full viewer.
//
// It streams from the same sidecar as BrowserViewer, but over the read-only
// `watch=1` path: a normal viewer connection TAKES control of the page, which
// would both block the agent's own clicks and (because the login hand-off
// watches control transitions) look like the user finished signing in. So the
// preview never sends anything, and the agent never notices it.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { Image as NativeImage } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { coworkerApi } from '@/lib/api';
import { useTranslation } from '@/hooks/useTranslation';

// Frames arrive at the sidecar's 1280x800 viewport; the thumbnail keeps that
// shape so pages are never squashed, and paints at 2x for a crisp image.
const PREVIEW_WIDTH = 212;
export const WORK_BROWSER_PREVIEW_HEIGHT = Math.round((PREVIEW_WIDTH * 800) / 1280);
const PREVIEW_HEIGHT = WORK_BROWSER_PREVIEW_HEIGHT;
const CANVAS_SCALE = 2;

const RECONNECT_BASE_MS = 4000;
const RECONNECT_MAX_MS = 30000;
// Sidecar frames arrive ~every 500ms; the rail thumbnail does not need that.
const FRAME_MIN_MS = 1000;

function hostnameOf(url) {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (_) {
    return String(url).replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
}

export default function WorkBrowserPreview(props) {
  // Replace the canvas and all stream state synchronously on chat changes.
  return <TaskBrowserPreview key={`${props.taskId || 'none'}:${props.previewId || ''}`} {...props} />;
}

function TaskBrowserPreview({ taskId, onOpen, isLive = false, style }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('connecting'); // connecting|live|unavailable
  const [url, setUrl] = useState('');
  const [frame, setFrame] = useState(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!taskId || !isLive) return undefined;

    let stopped = false;
    let ws = null;
    let retryTimer = null;
    let attempt = 0;
    let lastFrameAt = 0;

    // One decoder for the whole stream: frames land every ~500ms, so reusing
    // the image keeps this from allocating per frame.
    const img = Platform.OS === 'web' ? new Image() : null;
    if (img) img.onload = () => {
      const canvas = canvasRef.current;
      if (stopped || !canvas) return;
      canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
    };

    const connect = async () => {
      if (stopped) return;
      let wsUrl;
      try {
        wsUrl = await coworkerApi.getBrowserViewerUrl(taskId, { watch: true });
      } catch (_err) {
        if (!stopped) {
          setStatus('unavailable');
          retryTimer = setTimeout(connect, Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * (2 ** attempt++)));
        }
        return;
      }
      if (stopped) return;

      ws = new WebSocket(wsUrl);
      ws.onopen = () => {
        attempt = 0;
        if (!stopped) setStatus('live');
      };
      ws.onmessage = (ev) => {
        if (stopped) return;
        let msg = null;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === 'frame') {
          const now = Date.now();
          if (now - lastFrameAt < FRAME_MIN_MS) return;
          lastFrameAt = now;
          setFrame(msg.data);
          if (img) img.src = 'data:image/jpeg;base64,' + msg.data;
        }
        else if (msg.type === 'state' && msg.url) setUrl(msg.url);
      };
      // The sidecar starts on the agent's first browser call and stops when the
      // run settles, so a closed socket is expected — keep trying, slower each
      // time, while this thread is still working.
      const retry = () => {
        if (stopped) return;
        setStatus((prev) => (prev === 'live' ? 'connecting' : prev));
        const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * (2 ** attempt));
        attempt += 1;
        retryTimer = setTimeout(connect, delay);
      };
      ws.onclose = retry;
      ws.onerror = () => { try { ws.close(); } catch { /* already gone */ } };
    };

    connect();

    return () => {
      stopped = true;
      if (img) img.onload = null;
      if (retryTimer) clearTimeout(retryTimer);
      try { ws && ws.close(); } catch { /* already closed */ }
    };
  }, [taskId, isLive]);

  const handlePress = useCallback(() => { onOpen?.(); }, [onOpen]);

  const host = hostnameOf(url);
  const showFrame = !!frame || (isLive && status !== 'unavailable');

  return (
    <Pressable
      onPress={handlePress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={host ? `${t('chat.sourcesOutputs.openCloudBrowser')} — ${host}` : t('chat.sourcesOutputs.openCloudBrowser')}
      style={[
        styles.frame,
        {
          borderColor: hovered ? colors.border_color_primary : colors.border_color_secondary,
          backgroundColor: colors.solid_color_tertiary,
        },
        Platform.OS === 'web' && { cursor: 'pointer' },
        style,
      ]}
    >
      {showFrame && Platform.OS !== 'web' && frame ? <NativeImage source={{ uri: `data:image/jpeg;base64,${frame}` }} style={StyleSheet.absoluteFillObject} contentFit="contain" transition={0} /> : null}
      {showFrame && Platform.OS === 'web' ? (
        <canvas
          ref={canvasRef}
          width={PREVIEW_WIDTH * CANVAS_SCALE}
          height={PREVIEW_HEIGHT * CANVAS_SCALE}
          style={styles.canvas}
        />
      ) : null}

      {isLive && !frame && status === 'connecting' && (
        <View style={styles.center} pointerEvents="none">
          <ActivityIndicator size="small" color={colors.text_color_tertiary} />
        </View>
      )}

      {/* No live stream to show (settled run, or the browser never started):
          the tile still works as the way in. */}
      {!frame && (status === 'unavailable' || !isLive) && (
        <View style={styles.center} pointerEvents="none">
          <Icon name="window-maximize" size={14} color={colors.text_color_tertiary} />
          <Text size="xxs" color="tertiary">{t('chat.sourcesOutputs.openCloudBrowser')}</Text>
        </View>
      )}

      {!!host && status === 'live' && (
        <View style={styles.hostPill} pointerEvents="none">
          <Text size="xxs" color="primary" numberOfLines={1} style={styles.hostText}>{host}</Text>
        </View>
      )}
    </Pressable>
  );
}

WorkBrowserPreview.propTypes = {
  taskId: PropTypes.string,
  onOpen: PropTypes.func,
  isLive: PropTypes.bool,
  previewId: PropTypes.string,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

const styles = StyleSheet.create({
  frame: {
    height: PREVIEW_HEIGHT,
    marginHorizontal: 6,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
    justifyContent: 'center',
  },
  canvas: {
    width: '100%',
    height: '100%',
    display: 'block',
  },
  center: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  hostPill: {
    position: 'absolute',
    left: 6,
    bottom: 6,
    maxWidth: PREVIEW_WIDTH - 24,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
  },
  hostText: {
    color: '#fff',
  },
});
